import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FiUpload, FiX, FiArrowLeft, FiPlus } from 'react-icons/fi';
import API from '../../api/axios';
import { getImageUrl } from '../../utils/helpers';
import toast from 'react-hot-toast';

const AdminProductForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    name: '', description: '', shortDescription: '', price: '',
    comparePrice: '', stock: '', sku: '', brand: '', category: '',
    isFeatured: false, isActive: true, onSale: false, images: [], tags: '',
    specifications: [],
    packingInfo: { unitsPerCarton: '', cartonDimensions: '', cartonWeight: '', note: '' },
  });

  useEffect(() => {
    API.get('/categories/all').then(({ data }) => setCategories(data)).catch(() =>
      API.get('/categories').then(({ data }) => setCategories(data)).catch(() => {})
    );
    if (isEdit) {
      API.get(`/products/${id}`)
        .then(({ data }) => {
          setForm({
            name: data.name || '',
            description: data.description || '',
            shortDescription: data.shortDescription || '',
            price: data.price || '',
            comparePrice: data.comparePrice || '',
            stock: data.stock || '',
            sku: data.sku || '',
            brand: data.brand || '',
            category: data.category?._id || '',
            isFeatured: data.isFeatured || false,
            isActive: data.isActive !== false,
            images: data.images || [],
            tags: data.tags?.join(', ') || '',
            specifications: data.specifications || [],
            onSale: data.onSale || false,
            packingInfo: data.packingInfo || { unitsPerCarton: '', cartonDimensions: '', cartonWeight: '', note: '' },
          });
        })
        .catch(() => toast.error('Product not found'));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('images', f));
      const { data } = await API.post('/upload/multiple', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, images: [...f.images, ...data.urls] }));
      toast.success(`${files.length} image(s) uploaded`);
    } catch {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const handlePackingChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, packingInfo: { ...f.packingInfo, [name]: value } }));
  };

  const addSpec = () => setForm((f) => ({ ...f, specifications: [...f.specifications, { key: '', value: '' }] }));
  const updateSpec = (i, field, val) => {
    const specs = [...form.specifications];
    specs[i][field] = val;
    setForm({ ...form, specifications: specs });
  };
  const removeSpec = (i) => setForm((f) => ({ ...f, specifications: f.specifications.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.category || !form.description || !form.stock) {
      return toast.error('Please fill all required fields');
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        comparePrice: form.comparePrice ? Number(form.comparePrice) : undefined,
        stock: Number(form.stock),
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        specifications: form.specifications.filter((s) => s.key && s.value),
      };
      if (isEdit) await API.put(`/products/${id}`, payload);
      else await API.post('/products', payload);
      toast.success(isEdit ? 'Product updated!' : 'Product created!');
      navigate('/admin/products');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/products" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <FiArrowLeft size={18} />
        </Link>
        <h1 className="text-2xl font-bold text-gray-800">{isEdit ? 'Edit Product' : 'Add New Product'}</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main info */}
          <div className="lg:col-span-2 space-y-5">
            <div className="card p-6 space-y-4">
              <h2 className="font-bold text-gray-800">Product Information</h2>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Product Name *</label>
                <input name="name" value={form.name} onChange={handleChange} required className="input-field" placeholder="Enter product name" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Short Description</label>
                <input name="shortDescription" value={form.shortDescription} onChange={handleChange} className="input-field" placeholder="Brief description (shown on product card)" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Full Description *</label>
                <textarea name="description" value={form.description} onChange={handleChange} required rows={5} className="input-field" placeholder="Detailed product description..." />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Tags (comma separated)</label>
                <input name="tags" value={form.tags} onChange={handleChange} className="input-field" placeholder="e.g. hospital, monitor, ECG" />
              </div>
            </div>

            {/* Pricing & Stock */}
            <div className="card p-6 space-y-4">
              <h2 className="font-bold text-gray-800">Pricing & Inventory</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Price (PKR) *</label>
                  <input name="price" type="number" value={form.price} onChange={handleChange} required className="input-field" placeholder="0" min="0" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Compare Price (PKR)</label>
                  <input name="comparePrice" type="number" value={form.comparePrice} onChange={handleChange} className="input-field" placeholder="Original price" min="0" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Stock Quantity *</label>
                  <input name="stock" type="number" value={form.stock} onChange={handleChange} required className="input-field" placeholder="0" min="0" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">SKU</label>
                  <input name="sku" value={form.sku} onChange={handleChange} className="input-field" placeholder="Product SKU" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Brand</label>
                  <input name="brand" value={form.brand} onChange={handleChange} className="input-field" placeholder="Brand name" />
                </div>
              </div>
            </div>

            {/* Specifications */}
            <div className="card p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-bold text-gray-800">Specifications</h2>
                <button type="button" onClick={addSpec} className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium">
                  <FiPlus size={14} /> Add Spec
                </button>
              </div>
              {form.specifications.length === 0 && (
                <p className="text-sm text-gray-400 mb-3">No specifications added</p>
              )}
              {form.specifications.map((spec, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input value={spec.key} onChange={(e) => updateSpec(i, 'key', e.target.value)} className="input-field" placeholder="Key (e.g. Weight)" />
                  <input value={spec.value} onChange={(e) => updateSpec(i, 'value', e.target.value)} className="input-field" placeholder="Value (e.g. 2.5 kg)" />
                  <button type="button" onClick={() => removeSpec(i)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg"><FiX size={16} /></button>
                </div>
              ))}
            </div>

            {/* Packing Info */}
            <div className="card p-6 space-y-4">
              <div>
                <h2 className="font-bold text-gray-800">Packing Information</h2>
                <p className="text-xs text-gray-400 mt-0.5">Carton/shipping details shown on product page</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Units per Carton</label>
                  <input name="unitsPerCarton" value={form.packingInfo.unitsPerCarton} onChange={handlePackingChange} className="input-field" placeholder="e.g. 1pcs/carton" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Carton Dimensions</label>
                  <input name="cartonDimensions" value={form.packingInfo.cartonDimensions} onChange={handlePackingChange} className="input-field" placeholder="e.g. 79x48x27cm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Carton Weight</label>
                  <input name="cartonWeight" value={form.packingInfo.cartonWeight} onChange={handlePackingChange} className="input-field" placeholder="e.g. 13kgs" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Note</label>
                  <input name="note" value={form.packingInfo.note} onChange={handlePackingChange} className="input-field" placeholder="Additional packing note" />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* Status */}
            <div className="card p-6 space-y-3">
              <h2 className="font-bold text-gray-800">Status & Flags</h2>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} className="w-4 h-4 accent-primary-500" />
                <span className="text-sm text-gray-700">Active (visible on store)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="isFeatured" checked={form.isFeatured} onChange={handleChange} className="w-4 h-4 accent-primary-500" />
                <span className="text-sm text-gray-700">Featured Product</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="onSale" checked={form.onSale} onChange={handleChange} className="w-4 h-4 accent-red-500" />
                <span className="text-sm text-gray-700">On Sale <span className="text-xs text-red-500 font-semibold">(shows Sale badge)</span></span>
              </label>
            </div>

            {/* Category */}
            <div className="card p-6">
              <h2 className="font-bold text-gray-800 mb-3">Category *</h2>
              <select name="category" value={form.category} onChange={handleChange} required className="input-field">
                <option value="">Select category...</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Images */}
            <div className="card p-6">
              <h2 className="font-bold text-gray-800 mb-3">Product Images</h2>
              <label className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${uploading ? 'border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50'}`}>
                <FiUpload size={24} className="text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">{uploading ? 'Uploading...' : 'Click to upload images'}</span>
                <span className="text-xs text-gray-400 mt-1">PNG, JPG, WEBP up to 5MB each</span>
                <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
              </label>
              {form.images.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={getImageUrl(img)} alt="" className="w-full h-20 object-cover rounded-lg bg-gray-50" onError={(e) => { e.target.src = '/placeholder.png'; }} />
                      <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <FiX size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full py-3 flex items-center justify-center text-base">
              {loading ? <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" /> : (isEdit ? 'Update Product' : 'Create Product')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminProductForm;
